import '@banno/jha-wc/src/buttons/jha-button/jha-button';
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { CompanyUserService } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jha-wc';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
    CompanyUserChallengeDetailsViewModel,
    SuspensionHourOption,
} from '../../company-user-challenge-details.vm';

type Duration = { value: number; label: string };

@customElement('tm-bo-suspension-details')
export class SuspensionDetails extends TmBaseComponent {
    @property({ type: Object })
    public challengeDetails?: CompanyUserChallengeDetailsViewModel;

    @property({ type: Number })
    public userId?: number;

    @property({ type: Object })
    public company?: CompanyViewModel;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @state()
    private suspensionDuration?: Duration = { label: '15 minutes', value: 1 };

    public render() {
        if (!this.challengeDetails || !this.challengeDetails.isUserRegistered) {
            return nothing;
        }

        const {
            isAuthSuspended,
            suspensionHourOptions,
            authenticationSuspendedText,
            abbreviatedAuthenticationMethod,
        } = this.challengeDetails;

        return html`<hr class="mb-5" />
            <div class="mb-5 font-semibold">
                Authentication ${!isAuthSuspended ? 'Suspension' : 'Restoration'}
            </div>
            ${!isAuthSuspended
                ? html`<label>Suspend for: </label>
                      <div class="mb-5">
                          <tm-bo-form-select
                              .options=${suspensionHourOptions}
                              .labelFunction=${(option: SuspensionHourOption) => option.label}
                              name="suspension-hours"
                              @value-changed=${this.handleDurationChange}
                          >
                          </tm-bo-form-select>
                      </div>
                      <div class="mb-5">
                          <jh-button
                              label="Suspend"
                              @click=${this.handleSuspendUser}
                              appearance="primary"
                          ></jh-button>
                      </div>`
                : html`
                      <div class="mb-5">${authenticationSuspendedText}</div>
                      <div class="mb-5">
                          <jh-button
                              label=${`Restore ${abbreviatedAuthenticationMethod}`}
                              appearance="primary"
                              @click=${this.handleRestoreUser}
                          ></jh-button>
                      </div>
                  `}
            <hr class="mb-5" />`;
    }

    private handleDurationChange(e: CustomEvent<{ value: Duration }>) {
        this.suspensionDuration = e.detail.value;
    }

    private async handleRestoreUser(e: CustomEvent) {
        if (!this.challengeDetails || !this.company || !this.userId) {
            throw new Error("There was a issue restoring the user's authentication.");
        }

        const { abbreviatedAuthenticationMethod, authenticationSuspendedText } =
            this.challengeDetails;

        const selectElement = e.target as HTMLSelectElement;
        selectElement.disabled = true;

        const { reason } = await this.dialogService.open(
            html`<div>
                <p>${authenticationSuspendedText}</p>
                <p>
                    Are you sure you want to restore
                    ${abbreviatedAuthenticationMethod.toLowerCase()} authentication for this user?
                </p>
            </div>`,
            `Restore ${abbreviatedAuthenticationMethod}`,
            {
                buttons: [
                    {
                        label: 'Restore',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                    },
                    {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
            }
        );

        if (reason !== DynamicComponentExitReason.Confirm) {
            selectElement.disabled = false;
            return;
        }

        try {
            await this.companyUserService.restoreUserAuthentication(this.company.id, this.userId);
            this.toastService.show("The user's authentication was successfully restored");
        } catch {
            this.toastService.show(
                "There was an issue restoring the user's authentication",
                TmToastPosture.Danger
            );
        } finally {
            selectElement.disabled = false;
        }
    }

    private async handleSuspendUser(e: CustomEvent) {
        if (!this.challengeDetails || !this.company || !this.userId || !this.suspensionDuration) {
            throw new Error("There was an issue suspending the user's authentication.");
        }

        const { abbreviatedAuthenticationMethod } = this.challengeDetails;

        const selectElement = e.target as HTMLSelectElement;
        selectElement.disabled = true;

        const { reason } = await this.dialogService.open(
            html`<div>
                <p>
                    Are you sure you want to suspend
                    ${abbreviatedAuthenticationMethod.toLowerCase()} authentication for this user?
                </p>
            </div>`,
            `Suspend User`,
            {
                buttons: [
                    {
                        label: 'Suspend',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                    },
                    {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
            }
        );

        if (reason !== DynamicComponentExitReason.Confirm) {
            selectElement.disabled = false;
            return;
        }

        try {
            await this.companyUserService.suspendUserAuthentication(
                this.company.id,
                this.userId,
                this.suspensionDuration.value
            );

            let successMessage = '';
            if (this.suspensionDuration.value === 1) {
                successMessage = 'Authentication suspended for 15 minutes.';
            } else if (this.suspensionDuration.value === 2) {
                successMessage = 'Authentication suspended for 1 hour.';
            } else {
                successMessage = `Authentication suspended for ${this.suspensionDuration.label}.`;
            }

            this.toastService.show(successMessage, TmToastPosture.Success);

            this.dispatchEvent(new CustomEvent('suspended', { bubbles: true }));
        } catch {
            this.toastService.show(
                "There was an issue suspending the user's authentication.",
                TmToastPosture.Danger
            );
        } finally {
            selectElement.disabled = false;
        }
    }
}
